<script>
import Vue from 'vue'
import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
import AgGridDrillDownColumns from '@/Mixins/SigmaClientNew/AgGridDrillDownColumns'
import AgGridTaxableLineColumns from '@/Mixins/SigmaClientNew/AgGridTaxableLineColumns'
import { AgGridVue } from 'ag-grid-vue'
import { JournalEntryLineResource } from '@sigmacloud/sigma-client/dist/resources'
import GridAutocomplete from '@/components/SigmaClientNew/GridAutocomplete.vue'

export default Vue.extend({
  name: 'VendorTransactionsList',

  components: {
    AgGridVue,
    GridAutocomplete
  },
  mixins: [ AgGridMethods, AgGridTaxableLineColumns, AgGridDrillDownColumns ],

  props: {
    // Name of input field.  Not really necessary, but helpful if we use the form object down the road
    name: {
      required: true,
      type: String
    },

    iteration: {
      required: true,
      type: Number
    },

    width: {
      required: true,
      type: String
    },

    height: {
      required: true,
      type: String
    },

    classes: {
      required: false,
      type: String,
      default: 'ag-theme-balham'
    },

    rowData: {
      required: false
    },

    columns: {
      required: false,
      type: Array,
      default: undefined
    },

    summary: {
      required: false,
      type: Boolean,
      default: false
    },

    options: {
      required: false,
      type: Object,
      default: undefined
    },

    enableTaxableAmount: {
      required: false,
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isError: false, // If the input or selected field is incorrect, show an error
      isLoading: false,
      scope: this,
      gridApi: null,
      columnApi: null,
      defaultColDefs: undefined,
      defaultGridOptions: undefined,
      changes: []
    }
  },

  computed: {
    columnDefs () {
      if (this.columns && Array.isArray(this.columns)) {
        return this.columns
      } else if (this.summary) {
        return this.defaultSummaryColDefs
      } else {
        return this.defaultColDefs
      }
    },
    gridOptions () {
      return this.options || this.defaultGridOptions
    }
  },

  methods: {
    onGridReady (params) {
      if (!params) return
      this.gridApi = params.api
      this.columnApi = params.columnApi
    },

    onFirstDataRendered (params) {
      if (!params) return
      if (params.api.gridPanel.bodyHeight) params.api.sizeColumnsToFit()
      // this.onChangeVisibleColumns()
      // if (this.summary) this.pinSummaryTotalRow(this.gridApi)
    },

    onModelUpdated (params) {
    },

    onCellValueChanged (params) {
      // Ignore if not changed
      if ((params.value && params.oldValue === params.newValue) || !params.data) return
      // if (this.summary) this.pinSummaryTotalRow(this.gridApi)
    },

    onChangeVisibleColumns () {
      this.$nextTick(() => {
        this.gridApi.sizeColumnsToFit()
      })
    },

    exportToExcel () {
      this.gridApi.exportDataAsExcel()
    },

    async save () {
      if (this.changes.length === 0) return false
      this.createNotification('Info', `Saving changes... Please don't refresh`, 'info')

      for (const i in this.changes) {
        const change = this.changes[i]
        let resource = await change.resource.detail(change.id)
        if (change.dc) {
          resource = await JournalEntryLineResource.detail(resource.attributes.offset_by)
        }
        resource.set(change.field, change.newValue)

        // Meta data is being erased on patches.  Need to manually add the change.
        if (resource.attributes.meta_data) {
          if (typeof resource.changes === 'undefined') resource.changes = {}
          resource.changes.meta_data = resource.attributes.meta_data
        }
        resource.validate()
        try {
          await resource.save()
        } catch (apiError) {
          console.log(apiError)
          return false
        }
      }
      this.reportNotif.close()
      this.createNotification('Success', 'Changes saved', 'success')

      this.changes = []
    },

    // Notification popup box
    createNotification (title, message, type, url, fileType, filename, duration = 0) {
      if (!message) message = 'Changes saved'

      if (url) {
        const newEl = this.$createElement
        message = newEl('p', null, [
          newEl('span', null, message + ' ')
        ])
      }

      this.reportNotif = this.$message({
        title: title || 'Message',
        type: type || 'info',
        duration: duration,
        message: message,
        showClose: true,
        onClose: this.disableReportRun = false
      })
    }
  },
  watch: {
    enableTaxableAmount () {
      this.gridApi.redrawRows()
    }
  },
  beforeMount () {
    this.defaultColDefs = [
      this.taxableVendorGroupColumn,
      this.taxableVendorTypeColumn,
      this.viewTransactionColumn,
      this.transNumberColumn,
      this.paymentDateColumn,
      this.lineDescColumn,
      this.accountCodeColumn,
      this.taxableTaxCodeColumn,
      this.transWorkStateColumn,
      this.taxableTotalColumn,
      this.nonTaxableTotalColumn,
      this.amountGroupColumn
    ]
    this.defaultSummaryColDefs = [
      this.taxableVendorGroupColumn,
      this.amountGroupColumn,
      this.taxableTotalColumn,
      this.nonTaxableTotalColumn,
      this.taxableBoxNECColumn,
      this.taxableBoxNECGPPColumn,
      this.taxableBoxNECWHColumn,
      this.taxableBoxNECSTWHColumn,
      this.taxableBoxNEC7Column,
      this.taxableBox1Column,
      this.taxableBox2Column,
      this.taxableBox3Column,
      this.taxableBox4Column,
      this.taxableBox5Column,
      this.taxableBox6Column,
      this.taxableBox7Column,
      this.taxableBox8Column,
      this.taxableBox9Column,
      this.taxableBox10Column,
      this.taxableBox12Column,
      // this.taxableBox14Column,
      this.taxableBox15Column,
      this.taxableBox16Column,
      this.taxableBox18Column,
      this.taxableStateColumn
    ]
    this.defaultGridOptions = {
      singleClickEdit: false,
      undoRedoCellEditing: false,
      enterMovesDown: true,
      enterMovesDownAfterEdit: true,
      rowSelection: 'multiple',
      columnDefs: this.columnDefs,
      defaultColDef: {
        resizable: true,
        menuTabs: [],
        suppressSizeToFit: false
      },
      frameworkComponents: {
        gridAutocomplete: GridAutocomplete
      },
      pinnedBottomRowData: [],

      // Group Related
      autoGroupColumnDef: {
        headerName: 'Vendor',
        name: 'vendor',
        maxWidth: 250,
        minWidth: 100,
        cellRendererParams: {
          // checkbox: true,
          suppressCount: false
        },
        sortable: true,
        suppressSizeToFit: true,
        comparator: (nodeA, nodeB) => {
          if (nodeA.key < nodeB.key) {
            return -1
          } else if (nodeA.key > nodeB.key) {
            return 1
          } else {
            return 0
          }
        }
      },
      // groupUseEntireRow: true,
      groupDefaultExpanded: this.summary ? 0 : 1,
      // groupSelectsChildren: true,
      // enableRangeSelection: true,
      suppressAggFuncInHeader: true,
      groupRemoveLowestSingleChildren: false,
      aggFuncs: {
        sum: this.customSum, // this overrides the grids built in sum function
        list: this.customList // make a list of items
      },
      rowClassRules: {
        'ag-disabled': (params) => this.summary && this.enableTaxableAmount && params.node.aggData && parseFloat(params.node.aggData.taxable || 0) < 600,
        'ag-aggregateRow-1': (params) => params.node.aggData && params.node.level === 0
      },
      onGridReady: (params) => {
        this.onGridReady(params)
      },
      onFirstDataRendered: (params) => {
        this.onFirstDataRendered(params)
      },
      onModelUpdated: (params) => {
        this.onModelUpdated(params)
      },
      onCellValueChanged: (params) => {
        this.onCellValueChanged(params)
        // this.highlightCell()
      }
    }
  }
})
</script>

<template>
<div>
  <ag-grid-vue
    v-if="rowData"
    :name="name"
    :ref="name"
    :class="classes"
    :style="{ width, height }"
    :key="iteration"
    :column-defs="columnDefs"
    :row-data="rowData"
    :grid-options="gridOptions"
  />
</div>
</template>



// WEBPACK FOOTER //
// src/components/SigmaClientNew/VendorTransactionsList.vue